import { ValidationObserver, ValidationProvider } from "vee-validate";
import "@/plugins/vee-validate";
import { ServiceStatus } from "@/enums/ServiceStatus";
import { toTranslationKey } from "@/utils/stringFormat";
import moment from "moment";
import { PremiumServiceEventTypes } from "@/enums/PremiumServiceEventTypes";
import { getStatusColor } from "@/utils/getStatusColor";
import { ConciergeServiceType } from "@/enums/ConciergeServiceType";
import { ConciergeServicesListMixin } from "@/mixins/ConciergeServicesList.ts";
import ConciergeBookingItems from "../concierge-booking-items/ConciergeBookingItems.vue";

export default {
  name: "BookingsAndOrders",
  components: {
    ValidationObserver,
    ValidationProvider,
    ConciergeBookingItems
  },
  mixins: [ConciergeServicesListMixin],
  data() {
    return {
      loading: false,
      conciergeServiceType: ConciergeServiceType,
      conciergeRequestsTitle: this.$t("Concierge.ConciergeRequests"),
      conciergeEventsTitle: this.$t("Concierge.ConciergeEvents"),
      conciergeProductsTitle: this.$t("Concierge.ConciergeProducts"),
      isExpandedConciergeRequestsPreviewList:false,
      isExpandedConciergeEventsPreviewList:false,
      isExpandedConciergeProductsPreviewList:false,
      selectedServicesStatus: this.$store.state.additionalServicesModule.selectedServicesStatus,
      requestsCopy: null,
      eventsCopy: null,
      productsCopy: null,
      jsonFields: {
        Status: "status",
        "Requester": "requesterName",
        Resident: "residentName",
        Name: "serviceName",
        "Service Type": "serviceType",
        Dimension: "category",
        Provider: "providerName",
        "Date Booked": "createdDateTime",
        'Address': 'address',
        'Wing Name': 'wingName',
        'Room Name': 'roomName',
        'Phone': 'phone'
      },
      currentDate: new Date().getDate(),
      search: "",
      allStatusesFilter: [
        {
          status: "AllActive",
          text: "All active"
        },
        {
          status: "All",
          text: "All"
        },
        ...Object.entries(ServiceStatus).map(([key, value]) => ({
          status: value,
          color: getStatusColor(value),
          text: this.translateStatus(value)
         })),
      ],
      headers: [
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Status'],
          value: "status",
          sortable: false,
          width: 170
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['BookingsAndOrders.Requester'],
          align: "start",
          value: "requesterName",
          width: 170
        },  
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Resident'],
          align: "start",
          value: "residentName",
          width: 170
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Name'],
          align: "start",
          value: "serviceName",
          width: 170
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Type'],
          align: "start",
          value: "itemType",
          width: 170
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Dimension'],
          align: "start",
          value: "category",
          sortable: true,
          width: 170
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['ServiceList.Provider'],
          align: "start",
          value: "providerName",
          width: 170
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['ServiceList.DateBooked'],
          value: "createdDateTime",
          width: 170
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['ServiceList.RefNo'],
          align: "start",
          value: "id"
        },
      ]
    };
  },
  computed: {
    facility() {
      return this.$store.state.facilityModule.selectedFacility
    },
    serviceRequestFilter() {
      return this.$store.state.additionalServicesModule.serviceRequestFilter
    },
    serviceStatusFilter() {
      return this.$store.state.additionalServicesModule.serviceStatusFilter
    },
    serviceRequests() {
      return this.mergeResidentsToServiceRequests(this.$store.state.additionalServicesModule.serviceRequests, this.$store.state.residentModule.residentsShortData)
    },
    filteredServiceRequests(){
      let serviceRequests = this.serviceRequests

      
      if(this.selectedServicesStatus !== 'All' && this.selectedServicesStatus !== 'AllActive'){
        serviceRequests = serviceRequests.filter(x=>x.status === this.selectedServicesStatus)
      }

      if(this.selectedServicesStatus === 'AllActive'){
        serviceRequests = serviceRequests.filter(x=>x.status === ServiceStatus.AwaitingApproval || x.status === ServiceStatus.Approved)
      }

      if(this.search){
        serviceRequests = serviceRequests.filter(service => 
          service.serviceName.toLowerCase().indexOf(this.search.toLowerCase()) !== -1
          || service.providerName.toLowerCase().indexOf(this.search.toLowerCase()) !== -1
          || service.residentName.toLowerCase().indexOf(this.search.toLowerCase()) !== -1)
      }

if (this.selectedServicesStatus) {
  this.$store.commit('setSelectedStatus', this.selectedServicesStatus);

}

      return serviceRequests
    },
    requests(){
      const requests = this.$store.state.additionalServicesModule.additionalServices
      .filter(x => [
        PremiumServiceEventTypes.AppAccess,
        PremiumServiceEventTypes.ByAppointment,
        PremiumServiceEventTypes.Request
      ].includes(x.type));
      return requests
    },
    events(){
      const events = this.$store.state.additionalServicesModule.additionalServices.filter(x => [
        PremiumServiceEventTypes.Event,
        PremiumServiceEventTypes.EventBased,
        PremiumServiceEventTypes.ConciergeEvent,
      ].includes(x.type));
      return events
    },
    products(){
      const products = this.$store.state.additionalServicesModule.additionalServices.filter(x => [
        PremiumServiceEventTypes.Gift,
        PremiumServiceEventTypes.Product
      ].includes(x.type));
      return products
    },
    requestsRequests() {
     
      let requestsRequests = this.filteredServiceRequests

      const requestsIds = this.requests.map(x=>x.id)
      
      requestsRequests = requestsRequests.filter(x=> requestsIds.includes(x.serviceId) || x.serviceItemType === PremiumServiceEventTypes.Request).map(x=>({
        ...x,
        serviceName: x.serviceName || x.id,
        requesterName: x.bookByFamilyName || x.bookByResidentName || x.bookByStaffName,
        itemType: this.$t(`ConciergeServiceType.${ConciergeServiceType.ConciergeRequest}`),
        statusColor: getStatusColor(x.status),
        start: x.start ? moment(x.start.seconds * 1000).toDate(): null
      }))

      return requestsRequests
    }, 
    eventsRequests() {
     
      const eventsRequests = this.filteredServiceRequests

      const eventsIds = this.events.map(x=>x.id)

      return eventsRequests.filter(x=> eventsIds.includes(x.serviceId) || x.serviceItemType === PremiumServiceEventTypes.Event)
      .map((eventRequest)=>{
        const service = this.events.find(x=>x.id === eventRequest.serviceId)
        const event = {
          ...eventRequest,
          serviceName: eventRequest.serviceName || eventRequest.id,
          requesterName: eventRequest.bookByFamilyName || eventRequest.bookByResidentName || eventRequest.bookByStaffName,
          itemType: this.$t(`ConciergeServiceType.${ConciergeServiceType.ConciergeEvent}`),
          statusColor: getStatusColor(eventRequest.status),
          start: service?.start ? moment(service.start.seconds * 1000).toDate(): null
        }
        return event
      })
    },
   
    productsRequests() {

      let serviceRequests = this.filteredServiceRequests

      const productsIds = this.products.map(x=>x.id)
      
      serviceRequests = serviceRequests.filter(x=> productsIds.includes(x.serviceId) || x.serviceItemType === PremiumServiceEventTypes.Product).map(x=>({
        ...x,
        serviceName: x.serviceName || x.id,
        requesterName: x.bookByFamilyName || x.bookByResidentName || x.bookByStaffName,
        itemType: this.$t(`ConciergeServiceType.${ConciergeServiceType.ConciergeProduct}`),
        statusColor: getStatusColor(x.status)
      }))

      return serviceRequests
    },
    allConciergeItems(){
      const all = this.eventsRequests.concat(this.productsRequests).concat(this.requestsRequests).sort((a, b) => moment(b.createdDateTime).diff(moment(a.createdDateTime)));
      return all
    }
  },
  async created() {
    this.loading = true;
    await Promise.all([
      this.$store.dispatch("getAdditionalServices"),
      this.$store.dispatch("getServiceRequests")
    ]).then(() => {
      this.requestsCopy = [...this.requests]
      this.eventsCopy = [...this.events]
      this.productsCopy = [...this.products]
      this.loading = false;
    });
  },
  watch: {
    async selectedServicesStatus() {
      this.loading = true;
      await this.$store.dispatch("getServiceRequests")
      this.loading = false;
    }
  },
  methods: {
    mergeResidentsToServiceRequests(serviceRequests, residents) {
      return serviceRequests.map((sr) => {
        const resident = residents?.find((x) => x.id === sr.residentId);
        const serviceRequest = { ...sr };
        if(resident){
          serviceRequest.roomName = resident.roomName || '';
          serviceRequest.wingName = resident.wingName || '';
          serviceRequest.address = resident.address || '';
          serviceRequest.phone = resident.phone || '';
        }
        return serviceRequest
      });
    },
    handleClickService(row) {
      this.$router.push("bookings-and-orders/" + row.id);
    },
    convertToTranslationKey(text: string) {
      return toTranslationKey(text)
    },
    resetFilters() {
      this.selectedServicesStatus = "AllActive";
      this.search = ''
    },
    openRequest() {
      this.$router.push("services/" + this.createNewRequest.request.id);
    }
  }
};
